import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useOutletContext, useParams } from "react-router";

import GaiaConfirmActionDialog from "~/modules/app/components/GaiaConfirmActionDialog";
import useDestructiveActionBlocker from "~/modules/app/hooks/useDestructiveActionBlocker";
import useCurrentUser from "~/modules/auth/hooks/useCurrentUser";
import { GaiaMapUIOverlay } from "~/modules/map/components/GaiaMapUIOverlay";
import { MapButtonFitBounds } from "~/modules/map/components/MapButtonFitBounds";
import { AreasLayerV2 } from "~/modules/plannerV2/components/map-layers/AreasLayerV2";
import { PLANNER_MAP_PADDING } from "~/modules/plannerV2/constants/map";

import { PlannerV2ContextType } from "../types";
import { calculateReviewPlanScreenBounds } from "./calculateBounds";
import { ReviewPlanMapPanel } from "./components/ReviewPlanMapPanel";
import usePlannerV2AreasGetWithPendingChanges from "./hooks/usePlannerV2AreasGetWithPendingChanges";
import usePlannerV2ScheduleCancel from "./hooks/usePlannerV2ScheduleCancel";
import usePlannerV2ScheduleConfirm from "./hooks/usePlannerV2ScheduleConfirm";
import { useReviewPlanVisitGrouping } from "./hooks/useReviewPlanVisitGrouping";
import { useReviewPlanVisitSelection } from "./hooks/useReviewPlanVisitSelection";

export function PlannerV2ReviewPlanScreen() {
    const { date } = useParams();
    if (!date) throw new Error("invalid params");
    const { t } = useTranslation("plannerV2");

    const currentUser = useCurrentUser();

    const { plannerSharedLeftPanel } = useOutletContext<PlannerV2ContextType>();
    const {
        data: { areasToBeCancelled, areasToBePlanned, allPendingAreas, allPendingVisitIds, totalDrafts },
    } = usePlannerV2AreasGetWithPendingChanges({ onDate: date });

    const { confirmSchedule, loading: isConfirmingSchedule } = usePlannerV2ScheduleConfirm();
    const { cancelSchedule, loading: isCancelingSchedule } = usePlannerV2ScheduleCancel();
    const [successfullyConfirmedSchedule, setSuccessfullyConfirmedSchedule] = useState(false);

    const visitToUserId = useMemo(() => {
        const map = new Map<string, string | null>();
        for (const area of [...areasToBeCancelled, ...areasToBePlanned]) {
            for (const visit of area.plannerVisits) {
                map.set(visit.id, visit.stagedByUser.data?.id ?? null);
            }
        }
        return map;
    }, [areasToBeCancelled, areasToBePlanned]);

    const {
        selectedVisitIds,
        isAllSelected,
        toggleVisit,
        toggleAll,
        toggleUser,
        isUserFullySelected,
        isUserPartiallySelected,
        getUserSelectedCount,
    } = useReviewPlanVisitSelection(allPendingVisitIds, visitToUserId, currentUser?.id);

    const userGroups = useReviewPlanVisitGrouping(areasToBeCancelled, areasToBePlanned, currentUser?.id);

    const confirmSelectionBlocker = useDestructiveActionBlocker(async () => {
        if (selectedVisitIds.length === 0) return;
        await confirmSchedule(isAllSelected ? undefined : selectedVisitIds);
        setSuccessfullyConfirmedSchedule(true);
    });
    const cancelSelectionBlocker = useDestructiveActionBlocker(async () => {
        if (selectedVisitIds.length === 0) return;
        await cancelSchedule(isAllSelected ? undefined : selectedVisitIds);
    });
    const bounds = calculateReviewPlanScreenBounds(allPendingAreas);

    return (
        <>
            <GaiaConfirmActionDialog
                title={t("reviewPlanPanel.confirmDialogTitle")}
                description={t("reviewPlanPanel.confirmDialogDescription", { count: selectedVisitIds.length })}
                blocker={confirmSelectionBlocker}
            />
            <GaiaConfirmActionDialog
                title={t("reviewPlanPanel.cancelDialogTitle")}
                description={t("reviewPlanPanel.cancelDialogDescription", { count: selectedVisitIds.length })}
                blocker={cancelSelectionBlocker}
            />
            <GaiaMapUIOverlay
                leftPanel={plannerSharedLeftPanel}
                bottomRightPanel={
                    <ReviewPlanMapPanel
                        onReviewMoreClick={() => setSuccessfullyConfirmedSchedule(false)}
                        backHref=".."
                        visitHref={(plannerAreaId, plannerVisitId) =>
                            generatePath("/planner/:date/areas/:plannerAreaId/visits/:plannerVisitId", {
                                date,
                                plannerAreaId,
                                plannerVisitId,
                            })
                        }
                        areaNameHref={plannerAreaId =>
                            generatePath("/planner/:date/areas/:plannerAreaId", {
                                date,
                                plannerAreaId,
                            })
                        }
                        loading={isCancelingSchedule || isConfirmingSchedule}
                        isSuccessState={successfullyConfirmedSchedule}
                        userGroups={userGroups}
                        selectedVisitIds={selectedVisitIds}
                        isAllSelected={isAllSelected}
                        onToggleVisit={toggleVisit}
                        onToggleAll={toggleAll}
                        onToggleUser={toggleUser}
                        isUserFullySelected={isUserFullySelected}
                        isUserPartiallySelected={isUserPartiallySelected}
                        getUserSelectedCount={getUserSelectedCount}
                        onConfirmSelection={confirmSelectionBlocker.onAction}
                        onCancelSelection={cancelSelectionBlocker.onAction}
                        totalDraftVisits={totalDrafts}
                    />
                }
                bottomBar={<MapButtonFitBounds bounds={bounds} padding={PLANNER_MAP_PADDING} />}
            />
            <AreasLayerV2 areas={allPendingAreas} />
        </>
    );
}
